<template>
  <div class="trend-data-chart">
    <div id="parent-container">
        <div class="group" v-for="group in roomData.m0_groups" :key="group.group_id">
          <div class="group-row sdf-text-18-700-white">{{group.group_name}}</div>
          <div class="row item-row" v-for="item in group.m0_items" :key="item.item_id">
            <div class="channel-id-column">
              <div class="channel-id-text sdf-text-18-400-white">{{item.item_id}}</div>
            </div>
            <div class="channel-name-column">
              <div class="channel-name-text sdf-text-18-400-white">
                {{item.item_name}} 
              </div>
            </div>
            <div class="value-normal-column flex justify-end">
              <div class="unit-text sdf-text-18-400-white">{{ item.value_normal ? item.value_normal.replace('||', '〜') : "" }}</div>
            </div>
            <div class="value-column flex justify-end">
              <div class="value-text sdf-text-30-400-white">{{getSafelyDisplayMap(item.item_id).value}}</div>
            </div>
            <div class="unit-column">
              <div class="unit-text sdf-text-18-400-white">{{item.unit}}</div>
            </div>

            <div class="chart-column" :id="'container-'+item.item_id"></div>

            <div v-show="(item.value_string_option ? item.value_string_option.split(',') : []).length == 0">
              <feather-icon
                icon="PlusSquareIcon"
                svgClasses="h-6 w-6"
                class="mr-2"
                @click="yAxisZoom(item.item_id, 'plus')"
              />
              <br />
              <feather-icon
                icon="MinusSquareIcon"
                svgClasses="h-6 w-6"
                class="mr-2"
                @click="yAxisZoom(item.item_id, 'minus')"
              />
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script src="https://code.highcharts.com/modules/series-label.js"></script>
<script>
import Highcharts from 'highcharts'
import {secureDataFlowColors} from "@/../themeConfig.js"
import moment from "moment"
import SecureDataflowMixin from "@/mixins/SecureDataflowMixin"
import {createNamespacedHelpers} from "vuex"
const { mapGetters, mapActions, mapState } = createNamespacedHelpers("checklistTrendDataReport")

// 2値の場合の長方形プロットシンボル ( customRect2 ) を独自定義.
Highcharts.SVGRenderer.prototype.symbols.customRect2 = function (x, y, w, h) {
  const baseRate = 2;

  const yRate = 4.5 * 2 / baseRate ;
  return [
    'M',  x    , y + h / 2 - yRate * h / 2 , 
    'L',  x + w, y + h / 2 - yRate * h / 2 , 
          x + w, y + h / 2 + yRate * h / 2 ,
          x    , y + h / 2 + yRate * h / 2 ,
    'Z'];
    // X=0,Y=0 から 右下に向かって長方形を展開するような座標指定.
};
if (Highcharts.VMLRenderer) {
  Highcharts.VMLRenderer.prototype.symbols.customRect2 = Highcharts.SVGRenderer.prototype.symbols.customRect2;
}

// 3値の場合の長方形プロットシンボル ( customRect3 ) を独自定義.
Highcharts.SVGRenderer.prototype.symbols.customRect3 = function (x, y, w, h) {
  const baseRate = 3;

  const yRate = 4.5 * 2 / baseRate ;
  return [
    'M',  x    , y + h / 2 - yRate * h / 2 , 
    'L',  x + w, y + h / 2 - yRate * h / 2 , 
          x + w, y + h / 2 + yRate * h / 2 ,
          x    , y + h / 2 + yRate * h / 2 ,
    'Z'];
    // X=0,Y=0 から 右下に向かって長方形を展開するような座標指定.
};
if (Highcharts.VMLRenderer) {
  Highcharts.VMLRenderer.prototype.symbols.customRect3 = Highcharts.SVGRenderer.prototype.symbols.customRect3;
}

export default {
  name: "trendDataChart",
  mixins: [SecureDataflowMixin],
  props: [
    'xAxisValue',
  ],
  computed: {
    ...mapGetters([
      "getRooms",
      "getChannelTimeFrom",
      "getChannelTimeTo",
      "getLastChannelTimeFrom",
      "getLastChannelTimeTo",
      "getSelectedRoomId",
    ]),
    ...mapState({
      latestChannelsData: state => state.latestChannelsData,
    }),
    xAxisValueSync: {
      get() {
        return this.xAxisValue;
      },
      set(val) {
        this.$emit('update:xAxisValue', val);
      }
    },
    rooms_data: function (){
      return this.getRooms;
    },
    roomData: function (){
      if (this.getSelectedRoomId) {
        let index = _.findIndex(this.rooms_data.m0_rooms, (o) => { return o.room_id == this.getSelectedRoomId });
        if ( this.rooms_data.m0_rooms ) {
          return this.rooms_data.m0_rooms[index];
        }
        return [];
      } else {
        return [];
      }
    }
  },
  data: function () {
    return {
      xAxisStartValue: null,
      xAxisEndValue: null,
      chartsMap: {},
      displayMap: {},
    }
  },
  watch: {
    /**
     * 親コンポーネントからのチャンネルリストの変化に応じて再描画を行うトリガー.
     */
    roomData() {
      this.$nextTick(function () {
        this.redrawPointData();
        document.getElementsByClassName("trend-data-chart").forEach(element => {
          element.scrollTop = 0;
        });
      });
    },
  },
  methods: {
    /**
     * グラフ再描画.
     */
    redrawPointData: async function(){
      this.$vs.loading({
        text: "データ取得中",
        textAfter: true,
        color: this.sdfColor.Blue,
        container: '#content-area',
      })

      // getChannelTimeFrom getChannelTimeToで設定
      let startValue = moment(this.getLastChannelTimeFrom).set({hour:0,minute:0,second:0,millisecond:0})
      let endValue = moment(this.getLastChannelTimeTo).set({hour:23,minute:59,second:59,millisecond:999})
      this.xAxisStartValue = startValue.valueOf();
      this.xAxisEndValue = endValue.valueOf();

      if ( ! this.roomData.m0_groups ) {
        return;
      }

      let promiseAll = [];
      let firstViewFlg = true;
      let counter = 0;
      let firstBlockSize = 30;

      for ( let group of this.roomData.m0_groups) {
        for ( let item of group.m0_items) {

          const highChartsOptions = this.getHighChartsOption(this.xAxisStartValue, this.xAxisEndValue)
          let index = _.findIndex(this.rooms_data.m0_checklist_items, (o) => { return o.m0_item_id == item.item_id })

          // 最初のn個は真面目に表示処理を行う。
          if ( firstViewFlg === true ) {
            promiseAll.push(this.eachRedrawPointData(index,item,highChartsOptions));
          } else {
            // ファーストビューで表示されない部分はバックエンド実行（遅延実行で実現）。
            setTimeout( ()=> {
              this.eachRedrawPointData(index,item,highChartsOptions);
            }, 2000 + counter * 10 );
            counter++;
          }
          if ( promiseAll.length > firstBlockSize ) {
            await Promise.all(promiseAll);
            promiseAll = [];
            firstViewFlg = false;
          }
        }
      }

      await Promise.all(promiseAll);
      promiseAll = [];
      setTimeout( ()=> {
        this.$vs.loading.close('#content-area > .con-vs-loading')
      }, 1000);
    },

    /**
     * グラフ再描画並行処理の各実行.
     */
    eachRedrawPointData: async function(index,item,highChartsOptions) {

      let m0_checklist_item = (this.rooms_data.m0_checklist_items[index] ? this.rooms_data.m0_checklist_items[index] : {m0_item_id: item.item_id});
      let chart = await this.createChart(highChartsOptions, m0_checklist_item ,item);
      if ( !chart ) {
        // chartが作成できなかった場合（バックエンドで生成中に隣のルームに移動した場合など）は処理を中止。
        return;
      }
      const newPointDataList = this.convertPlotDataList(this.rooms_data.m0_checklist_items[index], item);

      chart.series.forEach(series => {
        series.setData(newPointDataList) ;
      });

      chart.redraw(); // 新しい座標データリストで再描画.

      // chartMapに最新のchart情報を記憶しておく
      this.chartsMap[item.item_id] = chart;
    },

    /**
     * チャートオブジェクトを生成.
     */
    createChart : async function(highChartsOption, checkData, item) {
      let self = this;
      if ( ! document.getElementById("container-" + checkData.m0_item_id)){
        // chartの生成先が存在しない場合（バックエンドで生成中に隣のルームに移動した場合など）は処理を中止。
        return;
      }

      let stringOptions = item.value_string_option ? item.value_string_option.split(',') : [];
      if (stringOptions.length > 0) {
        // ガントタイプ
        highChartsOption.chart.width = 560;
        highChartsOption.plotOptions.series.lineWidth = 0;
        highChartsOption.yAxis.categories = stringOptions;
        highChartsOption.yAxis.max = 1;
        highChartsOption.yAxis.min = 0;
        highChartsOption.yAxis.labels.x = -580;
        highChartsOption.yAxis.labels.y = 0;
        highChartsOption.yAxis.labels.align = 'left';
        highChartsOption.yAxis.tickInterval = 1;
      }

      // Highcharts実体化.
      const chart = Highcharts.chart("container-" + checkData.m0_item_id, highChartsOption);

      // ツールチップ表示イベントで、となりのカラムに詳細情報を転記するメソッドを登録
      chart.options.tooltip.formatter = function() {

        const displayMap = {
          value:  this.point.optionValue ? this.point.optionValue : this.point.y,
        };

        // $setを使って、updateイベントを手動発火させる。
        self.$set(self.displayMap, checkData.m0_item_id, displayMap);

        let time = moment(this.point.x).format("YYYY/MM/DD HH:mm:ss")
        return '<br/>' + time;
      }

      // サーバから受領して蓄積していたチャート座標データ群を、HighChartが理解しやすい形式に変換してセット.
      const newPointDataList = self.convertPlotDataList(checkData, item);
      chart.series[0].setData(newPointDataList);

      // あとで呼び出せるようにID情報を埋め込む
      chart.options.itemId = checkData.m0_item_id; 

      return chart;
    },

    /**
     * サーバから受領して蓄積していたチャート座標データ群を、HighChartが理解しやすい形式に変換.
     */
    convertPlotDataList: function(checkData, item) {
      if( ! checkData ) {
        return [];
      }
      const pointInfoList = checkData.m0_check_data
      if( ! pointInfoList ) {
        return [];
      }

      let stringOptions = item.value_string_option ? item.value_string_option.split(',') : [];

      let pointDataList = pointInfoList
        .map(pointInfo => {
          // value_string_option をみてイベントデータか数値データかを見分ける
          if ( stringOptions.length <= 0 ) {
            let yValue = pointInfo.value_number_round_digits
              ? Number(pointInfo.m0_value_number).toFixed(pointInfo.value_number_round_digits)
              : Number(pointInfo.m0_value_number)
            return {
              x: moment(pointInfo.m0_checked_at).valueOf(),
              y: yValue,
              marker: { radius: 4 },
            };
          } else {

            let optionValue = pointInfo.m0_value_string ? pointInfo.m0_value_string : pointInfo.m0_value_number;
            let optionIndex = _.findIndex(stringOptions, (s) => { return s == optionValue});

            // ガントタイプ
            return {
              x: moment(pointInfo.m0_checked_at).valueOf(),
              y: optionIndex,
              optionValue: optionValue,
              color: "#1F6D99", 
              colorIndex: 10,
              marker: {
                radius: 6 ,
                symbol: stringOptions.length === 2 ? 'customRect2' : 'customRect3', // 独自定義のシンボルを指定.
                states: {
                  hover: {
                    fillColor: this.sdfColor.Blue, 
                    radius: 6,
                  }
                }
              },
            };
          }
        });
      return pointDataList;
    },

    /**
     * x軸同期.
     * event: マウスイベント
     * type: イベントタイプ(over:軸表示処理、[else]:軸非表示処理)
     */
    xAxisSync: function(event, type) {
      const xAxisValue = event.target.x;
      let map = this.chartsMap;
      Object.keys(map).forEach((key) => {
        let chart = map[key];
        if ( ! chart.series ) {
          return;
        }
        chart.series.forEach((series) => {

          let matchedDataIndex = 0;
          for(let dataIndex = 0; dataIndex < series.data.length; dataIndex++) {
            if( series.data[dataIndex].x <= Math.ceil(xAxisValue / 1000 ) * 1000 ) {
              matchedDataIndex = dataIndex; // マウスイベントで特定したx座標を下回る限り上書き更新 = 基準チャートと同じx座標か、その直前のx座標を特定する。

              if (type === 'over' && series.data[dataIndex].x === xAxisValue && this.xAxisValue !== xAxisValue ) {
                // x軸情報は他のコンポーネントでも使うので、確定したら親コンポーネントに報告。
                this.xAxisValueSync = xAxisValue;
              }
            } 
          }

          let point = series.data[matchedDataIndex];
          if (type === 'over') {
            if ( chart.xAxis[0] ) { chart.xAxis[0].drawCrosshair(event, point); }
            if ( point ) { 
              point.setState('hover'); 
              chart.tooltip.refresh(point);
            }
          } else {
            if ( chart.xAxis[0] ) { chart.xAxis[0].hideCrosshair(); }
            if ( point ) {
              point.setState();
            }
            chart.tooltip.hide();
          }          
        });
      });
    },

    /**
     * グラフのY軸の表示範囲をズームイン・ズームアウトする.
     */
    yAxisZoom: function(channelId, mode) {
      let chart = this.chartsMap[channelId];

      const zoomValue = chart.options.zoomValue ? chart.options.zoomValue : 1;
      const newZoomValue = (mode === 'plus') 
              ? ( zoomValue > 1 ? zoomValue - 1 :  zoomValue / 2 )
              : ( zoomValue > 1 ? Math.floor(zoomValue + 1) :  zoomValue * 2 );

      const yAxisMinValue = chart.yAxis[0].getExtremes().dataMin; 
      const yAxisMaxValue = chart.yAxis[0].getExtremes().dataMax;


      // a:中央値基準でズームするパターン
      const yAxisDataRange = yAxisMaxValue - yAxisMinValue;
      const yAxisHalfValue = yAxisMinValue + ( yAxisDataRange / 2 ); 
      const newYAxisMaxValue = yAxisHalfValue + ( ( yAxisDataRange / 2 ) * newZoomValue );
      let newYAxisMinValue = yAxisHalfValue - ( ( yAxisDataRange / 2 ) * newZoomValue );
      newYAxisMinValue = newYAxisMinValue < 0 ? 0 : newYAxisMinValue; // データの最小値はマイナスにならない想定

      // b:下限値=０固定でズームするパターン
      // const newYAxisMinValue = 0;
      // const newYAxisMaxValue = yAxisMaxValue * newZoomValue;


      // 希望範囲をHighChartsにセット（その数値通りに採択されるかはHighCharts次第）
      chart.yAxis[0].setExtremes(newYAxisMinValue, newYAxisMaxValue);

      // ズーム値はチャートオブジェクトに記憶させておく。
      chart.options.zoomValue = newZoomValue;
    },

    /**
     * this.displayMapの中身を安全に取得.
     */
    getSafelyDisplayMap(channelId) {
      let displayMap = this.displayMap[channelId] ? this.displayMap[channelId] : {};
      if ( displayMap.value && String(displayMap.value).match(/^[0-9]*\.[0-9]{6,}$/) ){
        displayMap.value = Number(displayMap.value).toFixed(6);  // 小数点以下が多すぎる時の暫定補正.
      }      
      return displayMap;
    },

    /**
     * 共通のHighChartsオプション指定を取得.
     */
    getHighChartsOption : function (xAxisMinValue, xAxisMaxValue) {
      let self = this;

      // 共通のHighChartsオプション指定
      const highChartsOptions = {
        chart: {
          marginLeft: 0,
          spacingTop: 20,
          spacingBottom: 20,
          width: 600,
          height: 180,
        },
        title: {
          text: "",
        },
        credits: {
          enabled: false
        },
        legend: {
          enabled: false
        },
        xAxis: {
          min: xAxisMinValue,
          max: xAxisMaxValue,
          crosshair: true,
          labels: {
            enabled: false
          }
        },
        yAxis: {
          title: {
            text: null
          },
          opposite: true, // y軸の目盛を右側に出す
          labels:{ 
            align:'right', 
            x:0, 
          }, 
          offset : 40, // 目盛数値の表示幅を確保
        },
        tooltip: {
          borderWidth: 0,
          backgroundColor: 'none',
          pointFormat: '{point.x}',
          headerFormat: '',
          shadow: false,
        },
        plotOptions: {
          series: {
            lineWidth: 1, // 散布図でも線をつなげて表示する指定
            turboThreshold: 0, // [8640+]
            point: {
              events: {
                mouseOver: function(event) {
                  self.xAxisSync(event, 'over');
                },
                mouseOut: function(event) {
                  self.xAxisSync(event, 'out');
                }
              }
            }
          }
        },
        series: [{
          type: 'scatter', // デフォルトの'line'だとプロット数が増えるとドットが消えるので、散布図型で表示
          data: [], // 座標データは、各チャートに後からセット.
          fillOpacity: 0.3,
          tooltip: {
            valueSuffix: ' '// + dataset.unit
          }
        }],
      };
      return highChartsOptions;
    },
  },

  mounted: function () {
    if (this.roomData.length != 0) this.redrawPointData();
  },
}
</script>

<style lang="scss">
@import url("https://code.highcharts.com/css/highcharts.css");
@import "@/assets/scss/secure-dataflow/_variables.scss";
.error-chart {
  .highcharts-area {
    fill: $red !important;
  }
  .highcharts-graph {
    stroke: $red !important;
  }
}

.trend-data-chart {
  display: flex;
  overflow: scroll;
  height: calc(100vh - 300px);
  width: 100%;
  margin-right: 30px;
  .row {
    height: 140px;
    display: flex;
    align-items: center;
    margin-top: 10px;
    border-bottom: solid 0.5px #828282;
  }
  .group-row {
    background: #21556F;
    border-bottom: solid 0.5px #828282;
    height: 40px;
    position: sticky;
    z-index: 1;
    top: 0;
    font-size: 18px;
    display: flex;
    align-items: center;
    padding-left: 12px;
  }
  .highcharts-area {
    fill: $blue;
  }
  .highcharts-graph {
    stroke: $blue;
  }
  .highcharts-plot-background {
    stroke: $white;
    stroke-width: 1px;
  }
  .highcharts-background {
    fill: transparent;
  }
  .data-chart-container {
    margin-right: 5px;
    overflow: visible !important;
  }

  .highcharts-grid-line {
    stroke-width: 0px;
  }

  .alerm {
    width: 22px;
    height: 22px;
    border-radius: 19px;
    margin-left: auto;
    margin-right: auto;
    background: $sdf-red;
    border: 8px solid $sdf-red;
  }

  .alerm-column {
    min-width: 60px;
  }

  .channel-id-column {
    min-width: 60px;
  }

  .channel-id-text {
    text-align: center;
  }

  .channel-name-column {
    width: 360px;
  }
  .value-normal-column {
    min-width: 100px;
    padding-right: 0;
    font-size: 18px;
  }
  .value-column {
    min-width: 100px;
    padding-right: 1rem;
    font-size: 18px;
  }
  .unit-column {
    min-width: 80px;
    font-size: 22px;
  }
  .chart-column {
    min-width: 600px;
  }
  .value-text {
    font-size: 22px;
    line-height: 1;
  }
  .zoom-button-column {
    min-width: 17px;
  }
  .highcharts-point {
    stroke-width: 0px;
  }


  .error-status {
    background: $sdf-red;
  }
  .error-text {
    color: $sdf-red;
  }

  .zoom-in-button {
    display: block;
  }
  .chart-button {
    width: 17px;
    height: 17px;
    background: transparent;
    color: $sdf-white;
    border-width: 1px;
    border-color: $sdf-white;
    border-radius: 2px;
  }

  .highcharts-axis-labels {
    fill: $sdf-white;
  }

  .highcharts-grid-line {
    stroke: $sdf-blue;
  }

  .highcharts-tooltip-box {
    fill: $sdf-gray;
  }

  .highcharts-tooltip text {
    fill: $sdf-white !important;
  }

  .highcharts-markers {
    fill: $sdf-white;
  }
  
  .data-chart-container-blank {
    width: 605px;
  }

  .highcharts-tick {
    stroke: transparent;
  }

  .highcharts-graph {
    stroke: $sdf-white;
    stroke-width: 1px;
  }

  .highcharts-yaxis-labels text {
    fill: $sdf-white !important;
  }
}


</style>
